import React, { useEffect } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Radio,
  Row,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import { ICustomer } from '@/interfaces/customer';
import { addCustomer, editCustomer } from '@/adapters/customer';
import GeographySelector from '@/components/selector/geography-selector';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import { IGeography } from '@/interfaces/geography';
import { useAuth } from '@/contexts/AuthContext';
import { ECustomerType } from '@/enums/ECustomerType';
import UserSelector from '@/components/selector/user-selector';
import { EUserRole } from '@/enums/EUserRole';
import { getPaymentCategoryOptions } from '@/helpers/customerHelper';

interface IProps {
  dataModal?: IDataModal<Partial<ICustomer>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const CustomerAddModal = ({ dataModal, handleOk, handleCancel }: IProps) => {
  const { userInfo } = useAuth();
  const [form] = Form.useForm<Partial<ICustomer>>();
  const mutations = useMutation({
    mutationFn: async (data: Partial<ICustomer>) =>
      dataModal?.id ? await editCustomer(data) : await addCustomer(data),
    mutationKey: ['actionCustomer'],
    onSuccess: data => {
      if (data?.status !== 200) {
        throw new Error();
      } else {
        const message = dataModal?.id
          ? 'อัพเดทลูกค้าสำเร็จ'
          : 'สร้างลูกค้าสำเร็จ';
        notification.success({
          message: message,
        });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<ICustomer>) => {
        values.id = Number(dataModal?.id || 0);
        values.templates = dataModal?.item?.templates || '';
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (dataModal?.id) {
      form.setFieldsValue({
        ...dataModal?.item,
      });
    }
  }, [dataModal?.id, dataModal?.item, form]);

  const onSelectGeography = (item: IGeography, locale: 'th' | 'en') => {
    form.setFieldsValue({
      ...item,
      subdistrictNameTh:
        locale === 'th' ? item.subdistrictNameTh : item.subdistrictNameEn,
      districtNameTh:
        locale === 'th' ? item.districtNameTh : item.districtNameEn,
      provinceNameTh:
        locale === 'th' ? item.provinceNameTh : item.provinceNameEn,
      subdistrictCode: item.subdistrictCode,
      districtCode: item.districtCode,
      provinceCode: item.provinceCode,
    });
  };

  const _businessUnitId = Form.useWatch('businessUnitId', form);

  return (
    <Modal
      title={dataModal?.id ? 'รายละเอียดลูกค้า' : 'ลูกค้าใหม่'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={() => handleCancel()}
      closable={true}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={1200}
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="productGroup"
          loading={mutations.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="productGroup"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
          initialValues={{
            businessUnitId: userInfo?.bu,
            salesId:
              userInfo?.role === EUserRole.Employee ? userInfo?.id : undefined,
          }}
        >
          <Form.Item<ICustomer> name="subdistrictCode" hidden>
            <Input size="large" hidden />
          </Form.Item>
          <Form.Item<ICustomer> name="districtCode" hidden>
            <Input size="large" hidden />
          </Form.Item>
          <Form.Item<ICustomer> name="provinceCode" hidden>
            <Input size="large" hidden />
          </Form.Item>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label="ประเภทลูกค้า"
                name="customerType"
                rules={[{ required: true, message: 'ระบุประเภทลูกค้า' }]}
                initialValue={ECustomerType.DIRECT}
              >
                <Radio.Group>
                  <Radio value={ECustomerType.DIRECT}>Direct</Radio>
                  <Radio value={ECustomerType.DIS}>Distributor</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item<ICustomer>
                label="ประเภทวางบิลรับเงิน"
                name="paymentCategory"
                rules={[{ required: true, message: 'ระบุประเภทวางบิลรับเงิน' }]}
                // initialValue={
                //   EPaymentCategory.COLLECTOR_WITH_BILLING_BANK_TRANSFER
                // }
              >
                <Radio.Group>
                  {getPaymentCategoryOptions().map(c => {
                    return (
                      <Radio key={c.value} value={c.value}>
                        {c.label}
                      </Radio>
                    );
                  })}
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={24} md={8}>
              <Form.Item
                label="รหัสลูกค้า"
                name="code"
                rules={[{ required: true, message: 'ระบุรหัสลูกค้า' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={10}>
              <Form.Item
                label="ชื่อลูกค้า"
                name="name"
                rules={[{ required: true, message: 'ระบุชื่อลูกค้า' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={6}>
              <Form.Item<ICustomer> label="ผู้ขาย" name="salesId">
                <UserSelector
                  value={form.getFieldValue('salesId')}
                  businessUnitId={_businessUnitId}
                  handleSelectData={function (id: string): void {
                    form.setFieldValue('salesId', id);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24} md={6}>
              <Form.Item label="เลขผู้เสียภาษี" name="taxId">
                <Input size="large" maxLength={13} />
              </Form.Item>
            </Col>
            <Col span={24} md={6}>
              <Form.Item label="สาขา" name="branchId">
                <Input size="large" maxLength={13} />
              </Form.Item>
            </Col>
            <Col span={24} md={6}>
              <Form.Item<ICustomer>
                label="เครดิตเทอม (วัน)"
                name="creditTerm"
                rules={[{ required: true, message: 'ระบุเครดิตเทอม' }]}
              >
                <InputNumber size="large" min={0} className="w-full" />
              </Form.Item>
            </Col>
            <Col span={24} md={6}>
              <Form.Item<ICustomer>
                label="Business Unit"
                name="businessUnitId"
                rules={[{ required: true, message: 'ระบุ Business Unit' }]}
              >
                <BusinessUnitSelector
                  disabled={!!userInfo?.bu}
                  value={form.getFieldValue('businessUnitId')}
                  customClassname="md:w-full"
                  handleSelectData={id => {
                    form.setFieldsValue({
                      businessUnitId: id,
                      salesId: undefined,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <div className="flex w-full flex-row items-end gap-3">
                <div className="flex w-full">
                  <Form.Item label="ที่อยู่" name="address" className="w-full">
                    <Input size="large" />
                  </Form.Item>
                </div>
                <div className="flex">
                  <Form.Item label="" name="">
                    <GeographySelector
                      handleSelectData={onSelectGeography}
                      size="large"
                    />
                  </Form.Item>
                </div>
              </div>
            </Col>
            <Col span={24}>
              <Form.Item label="ถนน" name="road">
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="ตำบล" name="subdistrictNameTh">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="อำเภอ" name="districtNameTh">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="จังหวัด" name="provinceNameTh">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="รหัสไปรษณีย์" name="postalCode">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Google Map" name="maplink">
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="โทรศัพท์" name={'phoneNumber'}>
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            {/* <Col md={6}>
              <Form.Item<ICustomer>
                label="แนบ QR Code สินค้า"
                name="isProductQrCode"
                rules={[{ required: true, message: 'เลือกแนบ QR Code สินค้า' }]}
                initialValue={true}
              >
                <Radio.Group>
                  <Radio value={true}>ใช้งาน</Radio>
                  <Radio value={false}>ไม่ใช้งาน</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col md={6}>
              <Form.Item<ICustomer>
                label="เครือ CP"
                name="isCP"
                rules={[{ required: true }]}
                initialValue={true}
              >
                <Radio.Group>
                  <Radio value={true}>ใช่</Radio>
                  <Radio value={false}>ไม่ไช่</Radio>
                </Radio.Group>
              </Form.Item>
            </Col> */}
            <Col md={6}>
              <Form.Item
                label="สถานะ"
                name="isActive"
                rules={[{ required: true, message: 'เลือกสถานะ' }]}
                initialValue={true}
              >
                <Radio.Group>
                  <Radio value={true}>ใช้งาน</Radio>
                  <Radio value={false}>ไม่ใช้งาน</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default CustomerAddModal;
